/**
 * Board Settings Utilities
 * Helper functions for managing board configurations
 */

import store from '@/store';

/**
 * Initialize board settings on app start
 * Call this in main.ts or App.vue to ensure settings are loaded
 */
export async function initializeBoardSettings(): Promise<void> {
  try {
    if (!store.getters['boardSettings/isLoaded']) {
      await store.dispatch('boardSettings/initializeBoardSettings');
    }
  } catch (error) {
    // Failed to initialize board settings - using defaults
  }
}

/**
 * Get the current board configuration for a specific board type
 */
export function getBoardConfig(boardType: string) {
  return store.getters['boardSettings/getBoardConfig'](boardType);
}

/**
 * Check if a status ID is available for assignment
 */
export function isStatusAvailable(statusId: number, currentBoardType?: string, currentColumnId?: string): boolean {
  const assignment = store.getters['boardSettings/getStatusAssignment'](statusId);
  
  if (!assignment) {
    return true; // Not assigned anywhere
  }
  
  // If checking for current column, it's available for that column
  if (currentBoardType && currentColumnId) {
    return assignment.boardType === currentBoardType && assignment.columnTitle === currentColumnId;
  }
  
  return false; // Assigned elsewhere
}

/**
 * Get all used status IDs across all boards
 */
export function getAllUsedStatusIds(): number[] {
  return store.getters['boardSettings/getAllUsedStatusIds'];
}

/**
 * Reset board settings to defaults
 */
export async function resetBoardSettings(): Promise<void> {
  await store.dispatch('boardSettings/resetToDefaults');
}

/**
 * Export board settings as JSON string for backup
 */
export async function exportBoardSettings(): Promise<string> {
  return await store.dispatch('boardSettings/exportSettings');
}

/**
 * Import board settings from JSON string
 */
export async function importBoardSettings(settingsJson: string): Promise<boolean> {
  return await store.dispatch('boardSettings/importSettings', settingsJson);
}

/**
 * Utility to force refresh all board data
 */
export async function refreshAllBoards(): Promise<void> {
  try {
    await store.dispatch('board/loadBoardData');
  } catch (error) {
    // Failed to refresh board data - re-throwing for caller to handle
    throw error;
  }
}

/**
 * Debug utility to log current board settings state
 */
export function debugBoardSettings(): void {
  const state = store.state.boardSettings;
  // Debug information for board settings (commented out for production)
  // console.group('Board Settings Debug');
  // console.log('Is Loaded:', store.getters['boardSettings/isLoaded']);
  // console.log('Board Configs:', state.boardConfigs);
  // console.log('Last Saved:', state.lastSaved);
  // console.log('All Used Status IDs:', store.getters['boardSettings/getAllUsedStatusIds']);
  // console.groupEnd();
}

export default {
  initializeBoardSettings,
  getBoardConfig,
  isStatusAvailable,
  getAllUsedStatusIds,
  resetBoardSettings,
  exportBoardSettings,
  importBoardSettings,
  refreshAllBoards,
  debugBoardSettings
};
